import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type AdminCategoryOption,
  type AdminProductDetail,
  type AdminProductRow,
  type ProductIdentifierRow,
  type ProductPhoto,
  type Sector,
  type SpeciesKey,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

const TABS = [
  ['all', 'All'],
  ['published', 'Published'],
  ['draft', 'Drafts'],
] as const;

/**
 * Which side of the wall the curator is working on.
 *
 * "All" first and selected by default, because a curator hunting a barcode does
 * not yet know which side it is on — which is the search that matters most on
 * this screen. The other two are for working a queue.
 */
const SECTOR_TABS = [
  ['all', 'Both'],
  ['human', 'Human medicines'],
  ['veterinary', 'Animal health'],
] as const;

/** Only the animals worth a picker. The rest stay searchable, not clickable. */
const SPECIES_OPTIONS: ReadonlyArray<readonly [SpeciesKey, string]> = [
  ['cattle', 'Cattle'],
  ['poultry', 'Poultry'],
  ['goat', 'Goats'],
  ['sheep', 'Sheep'],
  ['pig', 'Pigs'],
  ['fish', 'Fish'],
  ['dog', 'Dogs'],
  ['cat', 'Cats'],
  ['horse', 'Horses'],
  ['rabbit', 'Rabbits'],
] as const;

/**
 * The catalogue itself.
 *
 * ── WHY THIS SCREEN HAD TO EXIST BEFORE ANY OF THE REST ─────────────────────
 *
 * Nothing could enter the catalogue except a database seeder — which also meant
 * no pharmacy's submission could ever be approved into it. Every other piece of
 * catalogue work was blocked on a form nobody had built.
 *
 * ── WHY CODES ARE A LIST AND NOT A FIELD ────────────────────────────────────
 *
 * A product collects codes over its life: a redesigned carton carries a new
 * barcode while the old one is still on shelves, and a re-registration issues a
 * second NAFDAC number. Adding a code never replaces one, so a pharmacist
 * holding a two-year-old box still scans straight to the right medicine.
 */
export function CatalogueScreen() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('all');
  const [sector, setSector] = useState<Sector | 'all'>('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<AdminProductRow | 'new' | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'catalogue', query, status, sector],
    queryFn: ({ signal }) => api.adminProducts({ q: query || undefined, status, sector }, signal),
    placeholderData: (previous) => previous,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalogue'] });

  if (isError) {
    return <AdminError title="We couldn't load the catalogue" error={error} onRetry={() => refetch()} />;
  }

  if (editing) {
    return (
      <ProductForm
        product={editing === 'new' ? null : editing}
        onDone={() => {
          setEditing(null);
          void refresh();
        }}
      />
    );
  }

  const products = data?.products ?? [];

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Catalogue</h1>
          <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
            The drug facts every pharmacy sells against. A product is the idea of a medicine — it
            holds no price, no stock and no expiry date; those belong to a pharmacy's listing and
            its batches.
          </p>
        </div>

        <Button onClick={() => setEditing('new')}>Add a product</Button>
      </header>

      <div className="mb-3">
        <label className="relative block">
          <span className="sr-only">Search the catalogue</span>
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, manufacturer, barcode or NAFDAC number"
            className="min-h-11 w-full rounded-btn border border-line bg-white pl-9 pr-3 text-[14px] text-ink outline-none focus:border-blue"
          />
        </label>
      </div>

      {/* The sector row sits ABOVE the status row, because it is the coarser
          cut: a curator picks a shop and then a state within it, not the other
          way round. */}
      <div className="mb-2 flex flex-wrap gap-1.5">
        {SECTOR_TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSector(key)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-semibold',
              sector === key
                ? key === 'veterinary'
                  ? 'border-blue bg-blue text-white'
                  : 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
            {data?.meta?.sector_counts?.[key] !== undefined ? (
              <span className="ml-1.5 opacity-70">{data.meta.sector_counts[key]}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatus(key)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
            {data?.meta?.counts?.[key] !== undefined ? (
              <span className="ml-1.5 opacity-70">{data.meta.counts[key]}</span>
            ) : null}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} data-skeleton className="h-[72px] w-full" radius="card" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          title={query ? 'Nothing matched' : 'The catalogue is empty'}
          body={
            query
              ? 'Try the barcode or the NAFDAC number — a curator usually has the pack in hand.'
              : 'Add the first product, or approve a pharmacy request.'
          }
          action={<Button onClick={() => setEditing('new')}>Add a product</Button>}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-card border border-line bg-white">
            {products.map((product) => (
              <ProductRow key={product.id} product={product} onEdit={() => setEditing(product)} />
            ))}
          </div>

          <p className="m-0 mt-3 text-[11.5px] text-faint">
            Showing {products.length} of {data?.meta?.total ?? products.length}.
          </p>
        </>
      )}
    </div>
  );
}

function ProductRow({ product, onEdit }: { product: AdminProductRow; onEdit: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-3 last:border-b-0">
      <button type="button" onClick={onEdit} className="min-w-[200px] flex-1 text-left">
        <span className="flex flex-wrap items-center gap-2">
          {/* The catalogue list is the one screen that shows both shops at
              once, so a row that does not say which one it is in is a row a
              curator can edit believing it is the other. */}
          {product.is_veterinary ? (
            <span className="rounded-pill border border-blue px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue">
              Animal
            </span>
          ) : null}
          <span className="text-[14px] font-semibold text-ink">{product.display_name}</span>
          {product.status === 'draft' ? <Badge tone="neutral">Draft</Badge> : null}
          {product.is_pom ? <Badge tone="caution">POM</Badge> : null}
          {product.is_cold_chain ? <Badge tone="brand">Cold chain</Badge> : null}
        </span>
        <span className="mt-0.5 block text-[12px] text-muted">
          {[product.form, product.pack_size, product.manufacturer].filter(Boolean).join(' · ') || '—'}
        </span>
        {/* Said out loud, because an assumed weight is invisible in the data
            and shows up as a delivery margin nobody can explain. */}
        {!product.weight_is_measured ? (
          <span className="mt-0.5 block text-[11px] text-caution-ink">
            No weight — ships at the {product.shipping_weight_grams}g default
          </span>
        ) : null}
      </button>

      <div className="min-w-[150px] text-[11.5px] text-faint">
        {product.identifiers.length > 0 ? (
          product.identifiers.map((identifier) => (
            <div key={identifier.id} className="font-mono">
              {identifier.value}
            </div>
          ))
        ) : (
          <span className="italic">No codes — cannot be scanned</span>
        )}
      </div>

      <span className="shrink-0 text-[12px] text-muted">
        {product.listings_count} {product.listings_count === 1 ? 'pharmacy' : 'pharmacies'}
      </span>
    </div>
  );
}

/**
 * Categories, grouped by shop, for the picker.
 *
 * Grouped rather than sorted, because a flat list of forty options where
 * "Poultry health" sits three rows below "Pain & fever" is a list somebody
 * mis-clicks — and mis-clicking here is what puts a cattle antibiotic on the
 * human shelf.
 */
function groupBySector(
  categories: AdminCategoryOption[],
): Array<[string, AdminCategoryOption[]]> {
  const human = categories.filter((category) => category.sector === 'human');
  const veterinary = categories.filter((category) => category.sector === 'veterinary');

  return [
    ['Human medicines', human],
    ['Animal health', veterinary],
  ].filter(([, options]) => (options as AdminCategoryOption[]).length > 0) as Array<
    [string, AdminCategoryOption[]]
  >;
}

/** Typed rather than a loose record, so a renamed field is a compile error. */
interface ProductFields {
  generic_name: string;
  brand_name: string;
  strength: string;
  form: string;
  pack_size: string;
  manufacturer: string;
  nafdac_reg_no: string;
  gtin: string;
  weight_grams: string;
  markup_override_bps: string;
  markup_cap_kobo: string;
  category_id: string;
  withdrawal_meat_days: string;
  withdrawal_milk_hours: string;
  withdrawal_eggs_days: string;
  withdrawal_note: string;
}

/**
 * The editor, and why it fetches before it renders.
 *
 * It used to be handed the row from the list, which carries the short fields
 * and nothing else — no species, no withdrawal periods, no GTIN, no markup
 * override. Those were therefore initialised EMPTY for an existing product and
 * then submitted, so editing a veterinary product's spelling wiped the meat,
 * milk and egg withdrawal periods along with it. That is the data that keeps a
 * treated animal out of the food chain, deleted by a typo fix.
 *
 * So an edit loads the full product first and the form waits for it. A new
 * product has nothing to load and renders immediately.
 */
function ProductForm({ product, onDone }: { product: AdminProductRow | null; onDone: () => void }) {
  const detail = useQuery({
    queryKey: ['admin', 'product', product?.id],
    queryFn: ({ signal }) => api.adminProduct(product!.id, signal),
    enabled: product !== null,
  });

  if (product && !detail.data) {
    return (
      <div className="mx-auto w-full max-w-[860px] space-y-3 px-4 py-6 xl:px-8">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-[420px] w-full" radius="card" />
      </div>
    );
  }

  return <LoadedProductForm product={detail.data ?? null} onDone={onDone} />;
}

function LoadedProductForm({
  product,
  onDone,
}: {
  product: AdminProductDetail | null;
  onDone: () => void;
}) {
  const [fields, setFields] = useState<ProductFields>({
    generic_name: product?.generic_name ?? '',
    brand_name: product?.brand_name ?? '',
    strength: product?.strength ?? '',
    form: product?.form ?? '',
    pack_size: product?.pack_size ?? '',
    manufacturer: product?.manufacturer ?? '',
    nafdac_reg_no: product?.nafdac_reg_no ?? '',
    gtin: '',
    weight_grams: product?.weight_grams ? String(product.weight_grams) : '',
    markup_override_bps: product?.markup_override_bps
      ? String(product.markup_override_bps)
      : '',
    markup_cap_kobo: product?.markup_cap_kobo ? String(product.markup_cap_kobo) : '',
    category_id: product?.category ? String(product.category.id) : '',
    withdrawal_meat_days: product?.withdrawal_meat_days
      ? String(product.withdrawal_meat_days)
      : '',
    withdrawal_milk_hours: product?.withdrawal_milk_hours
      ? String(product.withdrawal_milk_hours)
      : '',
    withdrawal_eggs_days: product?.withdrawal_eggs_days
      ? String(product.withdrawal_eggs_days)
      : '',
    withdrawal_note: product?.withdrawal_note ?? '',
  });

  const [species, setSpecies] = useState<SpeciesKey[]>(product?.species ?? []);
  const [isPom, setIsPom] = useState(product?.is_pom ?? false);
  const [isColdChain, setIsColdChain] = useState(product?.is_cold_chain ?? false);
  const [isControlled, setIsControlled] = useState(product?.is_controlled ?? false);
  const [reason, setReason] = useState('');

  // Both sides of the wall: a curator files into either, and the public
  // endpoint deliberately serves only one.
  const categories = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: ({ signal }) => api.adminCategories(signal),
  });

  const set = (key: keyof ProductFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));

  // ── The CATEGORY decides the sector, not a separate control ──────────────
  //
  // Which is why there is no sector field on this form. A product filed under
  // "Poultry health" is a veterinary product, full stop — the server enforces
  // it, and a second control that could disagree would only be a way to make
  // the form lie about what is about to be saved.
  const chosen = (categories.data ?? []).find(
    (category) => String(category.id) === fields.category_id,
  );
  const isVet = chosen ? chosen.sector === 'veterinary' : (product?.is_veterinary ?? false);

  const toggleSpecies = (key: SpeciesKey) =>
    setSpecies((current) =>
      current.includes(key) ? current.filter((one) => one !== key) : [...current, key],
    );

  const save = useMutation({
    mutationFn: () => {
      const number = (value: string) => (value.trim() === '' ? undefined : Number(value));

      const payload = {
        ...fields,
        category_id: Number(fields.category_id) || undefined,
        gtin: fields.gtin || undefined,
        weight_grams: fields.weight_grams.trim() === '' ? undefined : Number(fields.weight_grams),
        // Empty means "no override" and must reach the API as null, not as an
        // absent key — an absent key leaves whatever override is already on the
        // row, so a curator who clears the box would find it still there.
        markup_override_bps: number(fields.markup_override_bps) ?? null,
        markup_cap_kobo: number(fields.markup_cap_kobo) ?? null,
        is_pom: isPom,
        is_cold_chain: isColdChain,
        is_controlled: isControlled,

        // Sent only for a veterinary category. A human product carrying a
        // withdrawal period would be nonsense on the page and a bug in the
        // data, so the fields are not merely hidden — they are not sent.
        species: isVet ? species : undefined,
        withdrawal_meat_days: isVet ? number(fields.withdrawal_meat_days) : undefined,
        withdrawal_milk_hours: isVet ? number(fields.withdrawal_milk_hours) : undefined,
        withdrawal_eggs_days: isVet ? number(fields.withdrawal_eggs_days) : undefined,
        withdrawal_note: isVet ? fields.withdrawal_note || undefined : undefined,
      };

      return product ? api.updateProduct(product.id, payload) : api.createProduct(payload);
    },
    onSuccess: onDone,
  });

  const publish = useMutation({
    mutationFn: (status: 'draft' | 'published') =>
      api.setProductStatus(product!.id, status, reason || 'Catalogue review.'),
    onSuccess: onDone,
  });

  const error = (save.error ?? publish.error) instanceof ApiError
    ? ((save.error ?? publish.error) as ApiError)
    : null;
  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.4} />
        Back to the catalogue
      </button>

      <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">
        {product ? product.display_name : 'Add a product'}
      </h1>
      <p className="m-0 mt-1 max-w-[60ch] text-[13px] leading-relaxed text-muted">
        No price, no stock, no expiry date. A product is the medicine itself — a pharmacy's listing
        carries what they charge, and each batch carries its own expiry.
      </p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Generic name" value={fields.generic_name} onChange={set('generic_name')} error={fieldErrors.generic_name?.[0]} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Brand" value={fields.brand_name} onChange={set('brand_name')} />
          <Field label="Strength" value={fields.strength} onChange={set('strength')} />
          <Field label="Form" value={fields.form} onChange={set('form')} />
          <Field label="Pack size" value={fields.pack_size} onChange={set('pack_size')} />
          {/* What the pack weighs, which is what picks the delivery band.
              Without it everything ships at the platform default and the
              bands operations set are decorative — a 5-litre drinker and a
              blister of paracetamol cost the same to deliver. */}
          <Field
            label="Weight (grams)"
            hint={
              product && !product.weight_is_measured
                ? `Not measured — currently shipping at ${product.shipping_weight_grams}g.`
                : 'One unit, packed. A blister is about 20g; a 5-litre drinker about 1,400g.'
            }
            value={fields.weight_grams}
            onChange={set('weight_grams')}
            error={fieldErrors.weight_grams?.[0]}
          />
          {/* §1.5 — this one product's rate, where the category's is wrong
              for it. Left empty it inherits: product → pharmacy → category →
              platform default, which is where nearly everything should stay.
              An override is for the exception that would otherwise price
              itself out of the shop. */}
          <Field
            label="Markup override (bps)"
            hint="Leave empty to use the category rate. 1500 bps = 15% on top of the pharmacy's price."
            value={fields.markup_override_bps}
            onChange={set('markup_override_bps')}
            error={fieldErrors.markup_override_bps?.[0]}
          />
          <Field
            label="Most markup on one unit (kobo)"
            hint="For the expensive exception. 10% of a ₦2,000,000 theatre light is ₦200,000 and no hospital pays it."
            value={fields.markup_cap_kobo}
            onChange={set('markup_cap_kobo')}
            error={fieldErrors.markup_cap_kobo?.[0]}
          />

          <Field label="Manufacturer" value={fields.manufacturer} onChange={set('manufacturer')} />

          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Category</span>
            <select
              value={fields.category_id}
              onChange={(event) => set('category_id')(event.target.value)}
              className="min-h-12 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
            >
              <option value="">Choose one — it sets the shop and the commission rate</option>
              {groupBySector(categories.data ?? []).map(([label, options]) => (
                <optgroup key={label} label={label}>
                  {options.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.path}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {/* Stated plainly, because it is the consequence people miss: the
                category is what puts this on the human shelf or the animal
                one, and nothing later will ask them again. */}
            <span
              data-testid="category-sector-note"
              className={cn(
                'mt-1 block text-[11.5px] leading-relaxed',
                isVet ? 'font-medium text-blue' : 'text-muted',
              )}
            >
              {chosen
                ? isVet
                  ? 'This puts the product on the ANIMAL HEALTH shelf. Buyers reach it at /vet, and only VCN-registered sellers can list it.'
                  : 'This puts the product on the human medicines shelf.'
                : 'The category decides which shelf buyers find this on.'}
            </span>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="NAFDAC number"
            value={fields.nafdac_reg_no}
            onChange={set('nafdac_reg_no')}
            error={fieldErrors.nafdac_reg_no?.[0]}
            mono
          />
          <Field
            label={product ? 'Add a barcode' : 'Barcode'}
            hint={product ? 'Adding one never replaces an existing code.' : undefined}
            value={fields.gtin}
            onChange={set('gtin')}
            error={fieldErrors.gtin?.[0]}
            mono
          />
        </div>

        <section className="space-y-2">
          <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            What this medicine is
          </h2>
          <p className="m-0 text-[12.5px] leading-relaxed text-muted">
            These gate the pharmacist check and the capture policy, so they are never taken from a
            seller.
          </p>
          <Toggle
            label={isVet ? 'Veterinary prescription only' : 'Prescription only'}
            checked={isPom}
            onChange={setIsPom}
          />
          <Toggle label="Cold chain" checked={isColdChain} onChange={setIsColdChain} />
          <Toggle label="Controlled" checked={isControlled} onChange={setIsControlled} />
        </section>

        {isVet ? (
          <section className="space-y-3 rounded-card border border-blue p-4">
            <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-blue">
              Animal health facts
            </h2>
            <p className="m-0 text-[12.5px] leading-relaxed text-muted">
              Never taken from a seller. A farm shop typing its own withdrawal period is the one
              thing on a veterinary pack that could put antibiotic residue into milk.
            </p>

            <div>
              <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
                Licensed for
              </span>
              <div className="flex flex-wrap gap-1.5">
                {SPECIES_OPTIONS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={species.includes(key)}
                    onClick={() => toggleSpecies(key)}
                    className={cn(
                      'min-h-11 rounded-pill border px-3 text-[12.5px] font-medium',
                      species.includes(key)
                        ? 'border-blue bg-blue text-white'
                        : 'border-line bg-white text-muted hover:text-ink',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Meat (days)"
                value={fields.withdrawal_meat_days}
                onChange={set('withdrawal_meat_days')}
                error={fieldErrors.withdrawal_meat_days?.[0]}
              />
              <Field
                label="Milk (hours)"
                value={fields.withdrawal_milk_hours}
                onChange={set('withdrawal_milk_hours')}
                error={fieldErrors.withdrawal_milk_hours?.[0]}
              />
              <Field
                label="Eggs (days)"
                value={fields.withdrawal_eggs_days}
                onChange={set('withdrawal_eggs_days')}
                error={fieldErrors.withdrawal_eggs_days?.[0]}
              />
            </div>

            <Field
              label="Withdrawal note"
              hint="For anything the three numbers cannot say, such as a ban on use in dairy animals."
              value={fields.withdrawal_note}
              onChange={set('withdrawal_note')}
            />
          </section>
        ) : null}

        {error ? (
          <p role="alert" className="m-0 text-[12.5px] font-medium leading-relaxed text-danger">
            {error.message}
          </p>
        ) : null}

        <Button full type="submit" loading={save.isPending}>
          {product ? 'Save changes' : 'Add to the catalogue'}
        </Button>
      </form>

      {product ? <ProductPhotos product={product} /> : null}

      {product ? (
        <div className="mt-6 border-t border-line pt-5">
          <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {product.status === 'published' ? 'Withdraw from sale' : 'Publish'}
          </h2>
          <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
            This changes what buyers can find, so it is recorded with your name and your reason.
          </p>

          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why?"
            className="mt-3 min-h-12 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          />

          <Button
            variant="grey"
            className="mt-3"
            loading={publish.isPending}
            disabled={reason.trim().length < 4}
            onClick={() => publish.mutate(product.status === 'published' ? 'draft' : 'published')}
          >
            {product.status === 'published' ? 'Withdraw' : 'Publish'}
          </Button>

          {product.identifiers.length > 0 ? (
            <div className="mt-6">
              <h2 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
                Codes on this product
              </h2>
              <ul className="m-0 list-none space-y-1 p-0">
                {product.identifiers.map((identifier: ProductIdentifierRow) => (
                  <li key={identifier.id} className="text-[13px] text-ink">
                    <span className="font-mono">{identifier.value}</span>
                    <span className="ml-2 text-[11.5px] text-faint">
                      {identifier.label} · {identifier.source}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="relative flex min-h-11 cursor-pointer items-center gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
      <span
        aria-hidden
        className={cn(
          'grid h-[19px] w-[19px] shrink-0 place-items-center rounded-[6px] border transition-colors',
          checked ? 'border-blue bg-blue text-white' : 'border-line bg-white',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue',
        )}
      >
        {checked ? (
          <svg
            viewBox="0 0 12 10"
            aria-hidden
            className="h-[9px] w-[11px] fill-none stroke-current"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 5.2 4.3 8.5 11 1.6" />
          </svg>
        ) : null}
      </span>
      <span className="text-[13.5px] text-ink">{label}</span>
    </label>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  error,
  mono,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12.5px] font-medium text-ink">{label}</span>
      {hint ? <span className="mb-1.5 block text-[11.5px] leading-relaxed text-muted">{hint}</span> : null}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'min-h-12 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
          error ? 'border-danger' : 'border-line',
          mono && 'font-mono tracking-[0.02em]',
        )}
      />
      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : null}
    </label>
  );
}


/**
 * The pack photographs.
 *
 * ── WHY THIS SITS IN THE CATALOGUE AND NOT ON A LISTING ─────────────────────
 *
 * §9.1: drug facts are ChiCare's, never a seller's. A photograph is a drug
 * fact — it is the thing a buyer holds their delivery up against at the door,
 * and the whole counterfeit promise rests on the picture being the
 * manufacturer's pack rather than whatever a shop photographed in a back room.
 *
 * Sellers do send photographs, through the catalogue-request flow, where a
 * curator reads them and decides. That is a proposal. This is the record.
 */
function ProductPhotos({ product }: { product: AdminProductDetail }) {
  const queryClient = useQueryClient();
  const [photos, setPhotos] = useState<ProductPhoto[]>(product.photos ?? []);

  const refresh = (next: ProductPhoto[]) => {
    setPhotos(next);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'catalogue'] });
  };

  const add = useMutation({
    mutationFn: (file: File) => api.addProductPhoto(product.id, file),
    onSuccess: refresh,
  });

  const primary = useMutation({
    mutationFn: (id: number) => api.makeProductPhotoPrimary(product.id, id),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.removeProductPhoto(product.id, id),
    onSuccess: refresh,
  });

  const error = [add.error, primary.error, remove.error].find(
    (one): one is ApiError => one instanceof ApiError,
  );

  return (
    <div className="mt-6 border-t border-line pt-5">
      <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        Pack photographs
      </h2>
      <p className="m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed text-muted">
        What a buyer checks the pack against at the door. The first one is the pack shot — it is
        what shows in search and on the product page.
      </p>

      {photos.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {photos.map((photo) => (
            <div
              key={photo.id}
              data-testid={`photo-${photo.id}`}
              className={cn(
                'overflow-hidden rounded-card border bg-white',
                photo.is_primary ? 'border-blue' : 'border-line',
              )}
            >
              <img
                src={photo.url}
                alt={photo.alt ?? product.display_name}
                className="aspect-square w-full bg-paper object-contain"
              />

              <div className="flex items-center justify-between gap-1 border-t border-line px-2 py-1.5">
                {photo.is_primary ? (
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-blue">
                    Pack shot
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => primary.mutate(photo.id)}
                    className="min-h-11 bg-transparent px-0 text-[11.5px] font-semibold text-muted hover:text-blue"
                  >
                    Make main
                  </button>
                )}

                <button
                  type="button"
                  aria-label="Remove this photograph"
                  onClick={() => remove.mutate(photo.id)}
                  className="grid h-11 w-9 shrink-0 place-items-center bg-transparent text-muted hover:text-danger"
                >
                  <Icon name="trash" size={14} strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="m-0 mt-3 rounded-card border border-dashed border-line px-4 py-5 text-center text-[12.5px] text-muted">
          No photograph yet. A buyer checking a pack at the door has nothing to compare it to.
        </p>
      )}

      {error ? (
        <p role="alert" className="m-0 mt-2.5 text-[12.5px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <label className="mt-3 inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-btn border border-line bg-white px-3.5 text-[13px] font-semibold text-ink hover:border-blue">
        <Icon name="camera" size={15} strokeWidth={2} />
        {add.isPending ? 'Uploading…' : 'Add a photograph'}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          data-testid="product-photo-input"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];

            if (file) {
              add.mutate(file);
            }

            // Cleared so picking the same file twice still fires.
            event.target.value = '';
          }}
        />
      </label>

      <p className="m-0 mt-1.5 text-[11.5px] text-faint">
        PNG, JPG or WEBP, up to 4MB. Six per product.
      </p>
    </div>
  );
}
